import {
  FC,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react"
import cx from "classnames"
import { BiPlus as AddIcon } from "react-icons/bi"
import Button from "@/components/Button"
import ConfirmModal from "@/components/ConfirmModal"
import ErrorMessage from "@/components/ErrorMessage"
import Loader from "@/components/Loader"
import OutlineButton from "@/components/OutlineButton"
import Radio from "@/components/Radio"
import RadioGroup from "@/components/RadioGroup"
import Textarea from "@/components/Textarea"
import TextField from "@/components/TextField"
import { getErrorMessage } from "@/core/api/helpers"
import { useAppDispatch } from "@/redux/hooks"
import {
  useGetSequenceSpamTestPromptOverrideQuery,
  useResetSequenceSpamTestPromptOverrideMutation,
  useUpdateSequenceSpamTestPromptOverrideMutation,
} from "@/redux/services/sequencesApi"
import { setToast } from "@/redux/toastSlice"
import type { SequenceAPIResponse } from "@/types/entities/sequence"
import type {
  CopyFieldConfig,
  CopyFieldMode,
  Round,
  SignatureFieldConfig,
  SignatureFieldMode,
  Variation,
  VariationFields,
} from "@/types/entities/spamTestSettings"

type Props = {
  className?: string
  sequence: SequenceAPIResponse
}

type CopyFieldKey = "subject" | "greeting" | "body" | "signoff" | "unsub"
type SignatureFieldKey = "title" | "company" | "website" | "phoneNumber"
type SavingSection = `round-${number}` | "reset" | null

type RoundValidationErrors = {
  round?: string
  variationNames: Record<number, string>
  prompts: Record<string, string>
}

const COPY_FIELD_MODE_VALUES: CopyFieldMode[] = ["original", "prompt", "blank"]
const SIGNATURE_FIELD_MODE_VALUES: SignatureFieldMode[] = ["original", "blank"]

const COPY_FIELDS: {
  key: CopyFieldKey
  label: string
  promptPlaceholder: string
}[] = [
  {
    key: "subject",
    label: "Subject",
    promptPlaceholder: "Describe how AI should rewrite the subject.",
  },
  {
    key: "greeting",
    label: "Greeting",
    promptPlaceholder: "Describe how AI should rewrite the greeting.",
  },
  {
    key: "body",
    label: "Body",
    promptPlaceholder: "Describe how AI should rewrite the body.",
  },
  {
    key: "signoff",
    label: "Signoff",
    promptPlaceholder: "Describe how AI should rewrite the signoff.",
  },
  {
    key: "unsub",
    label: "Unsub",
    promptPlaceholder: "Describe how AI should rewrite the unsubscribe line.",
  },
]

const SIGNATURE_FIELDS: {
  key: SignatureFieldKey
  label: string
}[] = [
  { key: "title", label: "Title" },
  { key: "company", label: "Company" },
  { key: "website", label: "Website" },
  { key: "phoneNumber", label: "Phone" },
]

const createCopyField = (
  mode: CopyFieldMode = "original",
  prompt = "",
): CopyFieldConfig => ({ mode, prompt })

const createSignatureField = (
  mode: SignatureFieldMode = "original",
): SignatureFieldConfig => ({ mode })

const createFields = (
  copyOverrides: Partial<Record<CopyFieldKey, CopyFieldConfig>> = {},
  signatureOverrides: Partial<
    Record<SignatureFieldKey, SignatureFieldConfig>
  > = {},
): VariationFields => ({
  subject: copyOverrides.subject ?? createCopyField(),
  greeting: copyOverrides.greeting ?? createCopyField(),
  body: copyOverrides.body ?? createCopyField(),
  signoff: copyOverrides.signoff ?? createCopyField(),
  unsub: copyOverrides.unsub ?? createCopyField(),
  title: signatureOverrides.title ?? createSignatureField(),
  company: signatureOverrides.company ?? createSignatureField(),
  website: signatureOverrides.website ?? createSignatureField(),
  phoneNumber: signatureOverrides.phoneNumber ?? createSignatureField(),
})

const createVariation = (
  variationName?: string,
  copyOverrides: Partial<Record<CopyFieldKey, CopyFieldConfig>> = {},
  signatureOverrides: Partial<
    Record<SignatureFieldKey, SignatureFieldConfig>
  > = {},
): Variation => ({
  ...(variationName ? { variationName } : {}),
  fields: createFields(copyOverrides, signatureOverrides),
})

const isCopyFieldMode = (value: unknown): value is CopyFieldMode =>
  COPY_FIELD_MODE_VALUES.includes(value as CopyFieldMode)

const isSignatureFieldMode = (value: unknown): value is SignatureFieldMode =>
  SIGNATURE_FIELD_MODE_VALUES.includes(value as SignatureFieldMode)

const normalizeCopyField = (
  field?: Partial<CopyFieldConfig>,
): CopyFieldConfig => ({
  mode: isCopyFieldMode(field?.mode) ? field.mode : "original",
  prompt: typeof field?.prompt === "string" ? field.prompt : "",
})

const normalizeSignatureField = (
  field?: Partial<SignatureFieldConfig>,
): SignatureFieldConfig => ({
  mode: isSignatureFieldMode(field?.mode) ? field.mode : "original",
})

const normalizeVariation = (variation?: Partial<Variation>): Variation => ({
  ...(variation?.variationName
    ? { variationName: variation.variationName }
    : {}),
  fields: {
    subject: normalizeCopyField(variation?.fields?.subject),
    greeting: normalizeCopyField(variation?.fields?.greeting),
    body: normalizeCopyField(variation?.fields?.body),
    signoff: normalizeCopyField(variation?.fields?.signoff),
    unsub: normalizeCopyField(variation?.fields?.unsub),
    title: normalizeSignatureField(variation?.fields?.title),
    company: normalizeSignatureField(variation?.fields?.company),
    website: normalizeSignatureField(variation?.fields?.website),
    phoneNumber: normalizeSignatureField(variation?.fields?.phoneNumber),
  },
})

const normalizeRound = (round: Partial<Round>, index: number): Round => {
  const roundNumber = Number.isInteger(round.roundNumber)
    ? Number(round.roundNumber)
    : index + 1
  const variations = Array.isArray(round.variations)
    ? round.variations.map(normalizeVariation)
    : []

  return {
    roundNumber,
    variations: variations.length ? variations : [createVariation()],
  }
}

const sanitizeCopyField = (field: CopyFieldConfig): CopyFieldConfig => {
  return {
    mode: field.mode,
    prompt: field.mode === "prompt" ? field.prompt.trim() : "",
  }
}

const sanitizeSignatureField = (
  field: SignatureFieldConfig,
): SignatureFieldConfig => ({ mode: field.mode })

const sanitizeVariation = (variation: Variation): Variation => {
  const variationName = variation.variationName?.trim()

  return {
    ...(variationName ? { variationName } : {}),
    fields: {
      subject: sanitizeCopyField(variation.fields.subject),
      greeting: sanitizeCopyField(variation.fields.greeting),
      body: sanitizeCopyField(variation.fields.body),
      signoff: sanitizeCopyField(variation.fields.signoff),
      unsub: sanitizeCopyField(variation.fields.unsub),
      title: sanitizeSignatureField(variation.fields.title),
      company: sanitizeSignatureField(variation.fields.company),
      website: sanitizeSignatureField(variation.fields.website),
      phoneNumber: sanitizeSignatureField(variation.fields.phoneNumber),
    },
  }
}

const sanitizeRound = (round: Round): Round => ({
  roundNumber: round.roundNumber,
  variations: round.variations.map(sanitizeVariation),
})

const promptErrorKey = (variationIndex: number, field: CopyFieldKey) =>
  `${variationIndex}.${field}`

const validateRound = (round: Round): RoundValidationErrors => {
  const errors: RoundValidationErrors = {
    variationNames: {},
    prompts: {},
  }

  if (!round.variations.length) {
    errors.round = "At least 1 variation is required."
  }

  const variationNameIndexes = new Map<string, number[]>()
  round.variations.forEach((variation, variationIndex) => {
    const variationName = variation.variationName?.trim()
    if (!variationName) return

    variationNameIndexes.set(variationName, [
      ...(variationNameIndexes.get(variationName) ?? []),
      variationIndex,
    ])
  })

  variationNameIndexes.forEach((indexes) => {
    if (indexes.length < 2) return

    indexes.forEach((variationIndex) => {
      errors.variationNames[variationIndex] =
        "Variation names must be unique within this round."
    })
  })

  round.variations.forEach((variation, variationIndex) => {
    COPY_FIELDS.forEach(({ key, label }) => {
      const field = variation.fields[key]
      if (field.mode === "prompt" && !field.prompt?.trim()) {
        errors.prompts[
          promptErrorKey(variationIndex, key)
        ] = `${label} prompt is required.`
      }
    })
  })

  return errors
}

const roundHasErrors = ({
  round,
  variationNames,
  prompts,
}: RoundValidationErrors) =>
  Boolean(
    round || Object.keys(variationNames).length || Object.keys(prompts).length,
  )

const allSignatureFieldsBlank = (fields: VariationFields) =>
  SIGNATURE_FIELDS.every(({ key }) => fields[key].mode === "blank")

const SettingsSection: FC<{
  title: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}> = ({ title, actions, children, className }) => {
  const preventSummaryToggle = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <details
      open
      className={cx(
        "rounded-md border border-outline bg-[#F6F6F9] overflow-hidden",
        className,
      )}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 bg-white p-4 text-sm font-semibold marker:hidden [&::-webkit-details-marker]:hidden">
        <span>{title}</span>
        {actions && (
          <div
            className="flex flex-wrap items-center justify-end gap-3"
            onClick={preventSummaryToggle}
          >
            {actions}
          </div>
        )}
      </summary>
      <div className="border-t border-outline space-y-4 bg-[#F8F8F8] px-5 py-4">
        {children}
      </div>
    </details>
  )
}

const CopyFieldRow: FC<{
  roundNumber: number
  variationIndex: number
  fieldKey: CopyFieldKey
  label: string
  value: CopyFieldConfig
  promptPlaceholder: string
  promptError?: string
  disabled?: boolean
  onModeChange: (mode: CopyFieldMode) => void
  onPromptChange: (prompt: string) => void
}> = ({
  roundNumber,
  variationIndex,
  fieldKey,
  label,
  value,
  promptPlaceholder,
  promptError,
  disabled,
  onModeChange,
  onPromptChange,
}) => {
  const testIdPrefix = `round-${roundNumber}-variation-${
    variationIndex + 1
  }-${fieldKey}`

  return (
    <div
      className="rounded-md border border-outline bg-white p-3"
      data-testid={`${testIdPrefix}-field`}
    >
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[160px_minmax(0,1fr)] lg:items-center">
        <span className="text-sm font-medium">{label}</span>
        <RadioGroup
          value={value.mode}
          onChange={({ target: { value: mode } }) =>
            onModeChange(mode as CopyFieldMode)
          }
          className="flex flex-wrap gap-x-5 gap-y-2"
          disabled={disabled}
        >
          <Radio
            value="original"
            data-testid={`${testIdPrefix}-mode-original`}
            disabled={disabled}
          >
            Original
          </Radio>
          <Radio
            value="prompt"
            data-testid={`${testIdPrefix}-mode-prompt`}
            disabled={disabled}
          >
            Prompt
          </Radio>
          <Radio
            value="blank"
            data-testid={`${testIdPrefix}-mode-blank`}
            disabled={disabled}
          >
            Blank
          </Radio>
        </RadioGroup>
      </div>
      {value.mode === "prompt" && (
        <Textarea
          topLeftLabel={`${label} prompt`}
          value={value.prompt ?? ""}
          onChange={({ target: { value: prompt } }) => onPromptChange(prompt)}
          placeholder={promptPlaceholder}
          className="mt-3 min-h-[104px]"
          bottomLeftLabel={
            promptError ? (
              <span className="text-error">{promptError}</span>
            ) : null
          }
          disabled={disabled}
          data-testid={`${testIdPrefix}-prompt`}
        />
      )}
    </div>
  )
}

const SignatureFieldRow: FC<{
  roundNumber: number
  variationIndex: number
  fieldKey: SignatureFieldKey
  label: string
  value: SignatureFieldConfig
  disabled?: boolean
  onModeChange: (mode: SignatureFieldMode) => void
}> = ({
  roundNumber,
  variationIndex,
  fieldKey,
  label,
  value,
  disabled,
  onModeChange,
}) => {
  const testIdPrefix = `round-${roundNumber}-variation-${
    variationIndex + 1
  }-${fieldKey}`

  return (
    <div
      className="grid grid-cols-1 gap-3 rounded-md border border-outline bg-white p-3 lg:grid-cols-[160px_minmax(0,1fr)] lg:items-center"
      data-testid={`${testIdPrefix}-field`}
    >
      <span className="text-sm font-medium">{label}</span>
      <RadioGroup
        value={value.mode}
        onChange={({ target: { value: mode } }) =>
          onModeChange(mode as SignatureFieldMode)
        }
        className="flex flex-wrap gap-x-5 gap-y-2"
        disabled={disabled}
      >
        <Radio
          value="original"
          data-testid={`${testIdPrefix}-mode-original`}
          disabled={disabled}
        >
          Original
        </Radio>
        <Radio
          value="blank"
          data-testid={`${testIdPrefix}-mode-blank`}
          disabled={disabled}
        >
          Blank
        </Radio>
      </RadioGroup>
    </div>
  )
}

const VariationCard: FC<{
  roundNumber: number
  variation: Variation
  variationIndex: number
  variationCount: number
  errors?: RoundValidationErrors
  disabled?: boolean
  onVariationNameChange: (value: string) => void
  onCopyModeChange: (field: CopyFieldKey, mode: CopyFieldMode) => void
  onCopyPromptChange: (field: CopyFieldKey, prompt: string) => void
  onSignatureModeChange: (
    field: SignatureFieldKey,
    mode: SignatureFieldMode,
  ) => void
  onRemoveVariation: () => void
}> = ({
  roundNumber,
  variation,
  variationIndex,
  variationCount,
  errors,
  disabled,
  onVariationNameChange,
  onCopyModeChange,
  onCopyPromptChange,
  onSignatureModeChange,
  onRemoveVariation,
}) => {
  const fallbackName = `R${roundNumber}V${variationIndex + 1}`
  const displayName = variation.variationName?.trim() || fallbackName
  const testIdPrefix = `round-${roundNumber}-variation-${variationIndex + 1}`

  return (
    <div
      className="rounded-lg border border-outline bg-white p-4 shadow-sm"
      data-testid={`round-${roundNumber}-variation-card`}
    >
      <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
        <h3 className="text-base font-semibold">
          Variation {variationIndex + 1}: {displayName}
        </h3>
        <OutlineButton
          type="button"
          className="btn-sm"
          onClick={onRemoveVariation}
          disabled={disabled || variationCount <= 1}
          data-testid={`${testIdPrefix}-remove`}
        >
          Remove variation
        </OutlineButton>
      </div>

      <TextField
        topLeftLabel="Variation name (optional)"
        value={variation.variationName ?? ""}
        placeholder={fallbackName}
        onChange={({ target: { value } }) => onVariationNameChange(value)}
        className="input-sm"
        rootClassName="mt-4"
        bottomLeftLabel={
          errors?.variationNames[variationIndex] ? (
            <span className="text-error">
              {errors.variationNames[variationIndex]}
            </span>
          ) : null
        }
        disabled={disabled}
        data-testid={`${testIdPrefix}-name`}
      />

      <div className="mt-4 space-y-3">
        <h4 className="text-sm font-semibold">Copy fields</h4>
        {COPY_FIELDS.map(({ key, label, promptPlaceholder }) => (
          <CopyFieldRow
            key={key}
            roundNumber={roundNumber}
            variationIndex={variationIndex}
            fieldKey={key}
            label={label}
            value={variation.fields[key]}
            promptPlaceholder={promptPlaceholder}
            promptError={errors?.prompts[promptErrorKey(variationIndex, key)]}
            disabled={disabled}
            onModeChange={(mode) => onCopyModeChange(key, mode)}
            onPromptChange={(prompt) => onCopyPromptChange(key, prompt)}
          />
        ))}
      </div>

      <div className="mt-4 space-y-3">
        <h4 className="text-sm font-semibold">Signature sub-fields</h4>
        {SIGNATURE_FIELDS.map(({ key, label }) => (
          <SignatureFieldRow
            key={key}
            roundNumber={roundNumber}
            variationIndex={variationIndex}
            fieldKey={key}
            label={label}
            value={variation.fields[key]}
            disabled={disabled}
            onModeChange={(mode) => onSignatureModeChange(key, mode)}
          />
        ))}
      </div>

      {(variation.fields.unsub.mode === "blank" ||
        allSignatureFieldsBlank(variation.fields)) && (
        <div className="mt-4 space-y-2">
          {variation.fields.unsub.mode === "blank" && (
            <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
              Blank unsubscribe may affect compliance.
            </p>
          )}
          {allSignatureFieldsBlank(variation.fields) && (
            <p className="rounded-md border border-info/40 bg-info/10 px-3 py-2 text-sm text-info">
              Full signature removed.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

const RoundSection: FC<{
  round: Round
  errors?: RoundValidationErrors
  disabled?: boolean
  savingSection: SavingSection
  onAddVariation: () => void
  onSaveRound: () => void
  onRemoveVariation: (variationIndex: number) => void
  onVariationNameChange: (variationIndex: number, value: string) => void
  onCopyModeChange: (
    variationIndex: number,
    field: CopyFieldKey,
    mode: CopyFieldMode,
  ) => void
  onCopyPromptChange: (
    variationIndex: number,
    field: CopyFieldKey,
    prompt: string,
  ) => void
  onSignatureModeChange: (
    variationIndex: number,
    field: SignatureFieldKey,
    mode: SignatureFieldMode,
  ) => void
}> = ({
  round,
  errors,
  disabled,
  savingSection,
  onAddVariation,
  onSaveRound,
  onRemoveVariation,
  onVariationNameChange,
  onCopyModeChange,
  onCopyPromptChange,
  onSignatureModeChange,
}) => {
  const roundNumber = round.roundNumber

  return (
    <SettingsSection
      title={`Elimination Round ${roundNumber}`}
      actions={
        <OutlineButton
          type="button"
          className="btn-sm"
          onClick={onAddVariation}
          disabled={disabled}
          data-testid={`round-${roundNumber}-add-variation`}
        >
          <AddIcon className="h-5 w-5" />
          <span>Add Variation</span>
        </OutlineButton>
      }
    >
      {errors?.round && (
        <p className="rounded-md border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
          {errors.round}
        </p>
      )}
      <div className="space-y-4">
        {round.variations.map((variation, variationIndex) => (
          <VariationCard
            key={`${roundNumber}-${variationIndex}`}
            roundNumber={roundNumber}
            variation={variation}
            variationIndex={variationIndex}
            variationCount={round.variations.length}
            errors={errors}
            disabled={disabled}
            onVariationNameChange={(value) =>
              onVariationNameChange(variationIndex, value)
            }
            onCopyModeChange={(field, mode) =>
              onCopyModeChange(variationIndex, field, mode)
            }
            onCopyPromptChange={(field, prompt) =>
              onCopyPromptChange(variationIndex, field, prompt)
            }
            onSignatureModeChange={(field, mode) =>
              onSignatureModeChange(variationIndex, field, mode)
            }
            onRemoveVariation={() => onRemoveVariation(variationIndex)}
          />
        ))}
      </div>
      <Button
        type="button"
        onClick={onSaveRound}
        disabled={disabled}
        loading={savingSection === `round-${roundNumber}`}
        className="btn-sm min-w-[112px]"
        data-testid={`round-${roundNumber}-save`}
      >
        Save Round {roundNumber}
      </Button>
    </SettingsSection>
  )
}

const SequencePromptsPanel: FC<Props> = ({ className, sequence }: Props) => {
  const dispatch = useAppDispatch()
  const { data, isLoading, isError } =
    useGetSequenceSpamTestPromptOverrideQuery({ id: sequence._id })
  const [updatePromptOverride, { isLoading: isSaving }] =
    useUpdateSequenceSpamTestPromptOverrideMutation()
  const [resetPromptOverride, { isLoading: isResetting }] =
    useResetSequenceSpamTestPromptOverrideMutation()
  const [roundsForm, setRoundsForm] = useState<Round[]>([])
  const [roundSaveAttempted, setRoundSaveAttempted] = useState<
    Record<number, boolean>
  >({})
  const [savingSection, setSavingSection] = useState<SavingSection>(null)
  const [resetOverrideOpen, setResetOverrideOpen] = useState(false)

  useEffect(() => {
    if (!data) return

    setRoundsForm(data.rounds.map(normalizeRound))
    setRoundSaveAttempted({})
  }, [data])

  const roundValidationByNumber = useMemo(
    () =>
      roundsForm.reduce<Record<number, RoundValidationErrors>>(
        (validationByRound, round) => ({
          ...validationByRound,
          [round.roundNumber]: validateRound(round),
        }),
        {},
      ),
    [roundsForm],
  )

  const formDisabled = isSaving || isResetting

  const updateRound = (
    roundNumber: number,
    updater: (round: Round) => Round,
  ) => {
    setRoundsForm((current) =>
      current.map((round) =>
        round.roundNumber === roundNumber ? updater(round) : round,
      ),
    )
  }

  const updateVariation = (
    roundNumber: number,
    variationIndex: number,
    updater: (variation: Variation) => Variation,
  ) => {
    updateRound(roundNumber, (round) => ({
      ...round,
      variations: round.variations.map((variation, index) =>
        index === variationIndex ? updater(variation) : variation,
      ),
    }))
  }

  const handleAddVariation = (roundNumber: number) => {
    updateRound(roundNumber, (round) => ({
      ...round,
      variations: [...round.variations, createVariation()],
    }))
  }

  const handleRemoveVariation = (
    roundNumber: number,
    variationIndex: number,
  ) => {
    const round = roundsForm.find((round) => round.roundNumber === roundNumber)
    if (!round || round.variations.length <= 1) return

    const variation = round.variations[variationIndex]
    const variationName =
      variation.variationName?.trim() || `R${roundNumber}V${variationIndex + 1}`

    if (!window.confirm(`Remove variation ${variationName}?`)) return

    updateRound(roundNumber, (currentRound) => ({
      ...currentRound,
      variations: currentRound.variations.filter(
        (_, index) => index !== variationIndex,
      ),
    }))
  }

  const handleVariationNameChange = (
    roundNumber: number,
    variationIndex: number,
    value: string,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      variationName: value,
    }))
  }

  const handleCopyModeChange = (
    roundNumber: number,
    variationIndex: number,
    field: CopyFieldKey,
    mode: CopyFieldMode,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      fields: {
        ...variation.fields,
        [field]: {
          ...variation.fields[field],
          mode,
        },
      },
    }))
  }

  const handleCopyPromptChange = (
    roundNumber: number,
    variationIndex: number,
    field: CopyFieldKey,
    prompt: string,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      fields: {
        ...variation.fields,
        [field]: {
          ...variation.fields[field],
          prompt,
        },
      },
    }))
  }

  const handleSignatureModeChange = (
    roundNumber: number,
    variationIndex: number,
    field: SignatureFieldKey,
    mode: SignatureFieldMode,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      fields: {
        ...variation.fields,
        [field]: {
          ...variation.fields[field],
          mode,
        },
      },
    }))
  }

  const handleSaveRound = async (roundNumber: number) => {
    const round = roundsForm.find((round) => round.roundNumber === roundNumber)
    if (!round) return

    // Saving sends every round, so surface validation errors across all rounds
    // and block the request if any round is invalid, not just the clicked one.
    setRoundSaveAttempted(
      roundsForm.reduce<Record<number, boolean>>(
        (attempted, current) => ({ ...attempted, [current.roundNumber]: true }),
        {},
      ),
    )

    const hasInvalidRound = roundsForm.some((current) =>
      roundHasErrors(validateRound(current)),
    )
    if (hasInvalidRound) return

    const sanitizedRounds = roundsForm.map(sanitizeRound)
    setSavingSection(`round-${roundNumber}`)
    try {
      const saved = await updatePromptOverride({
        id: sequence._id,
        body: { rounds: sanitizedRounds },
      }).unwrap()
      setRoundsForm(saved.rounds.map(normalizeRound))
      setRoundSaveAttempted((current) => ({
        ...current,
        [roundNumber]: false,
      }))
      dispatch(
        setToast({
          show: true,
          variant: "success",
          message: "Sequence prompts saved",
        }),
      )
    } catch (error) {
      dispatch(
        setToast({
          show: true,
          variant: "error",
          message: getErrorMessage(error, true),
        }),
      )
    } finally {
      setSavingSection(null)
    }
  }

  const handleConfirmResetOverride = async () => {
    setSavingSection("reset")
    try {
      const saved = await resetPromptOverride({ id: sequence._id }).unwrap()
      setRoundsForm(saved.rounds.map(normalizeRound))
      setRoundSaveAttempted({})
      dispatch(
        setToast({
          show: true,
          variant: "success",
          message: "Sequence prompts reset to system defaults",
        }),
      )
    } catch (error) {
      dispatch(
        setToast({
          show: true,
          variant: "error",
          message: getErrorMessage(error, true),
        }),
      )
    } finally {
      setSavingSection(null)
    }
  }

  if (isLoading) {
    return (
      <div
        className={cx(
          "flex min-h-[292px] items-center justify-center",
          className,
        )}
        data-testid="sequence-prompts-panel"
      >
        <Loader />
      </div>
    )
  }

  if (isError) {
    return <ErrorMessage />
  }

  return (
    <>
      <div
        className={cx("flex flex-col gap-4", className)}
        data-testid="sequence-prompts-panel"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold leading-8">
              Sequence Prompts
            </h2>
            {data?.hasOverride === false && (
              <p className="mt-1 text-sm text-base-content/70">
                This sequence uses the system default prompts. Saving a round
                creates a per-sequence override.
              </p>
            )}
            {data?.hasOverride === true && (
              <p className="mt-1 text-sm text-base-content/70">
                This sequence has custom prompts overriding the system defaults.
              </p>
            )}
          </div>
          {data?.hasOverride === true && (
            <OutlineButton
              type="button"
              className="btn-sm"
              onClick={() => setResetOverrideOpen(true)}
              disabled={formDisabled}
              loading={savingSection === "reset"}
              data-testid="reset-sequence-prompts"
            >
              Reset to system defaults
            </OutlineButton>
          )}
        </div>

        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Elimination Rounds</h3>
          <p className="text-sm text-base-content/70">
            Manage the copy and signature variations tested for this sequence.
          </p>
        </div>

        {roundsForm.map((round) => (
          <RoundSection
            key={round.roundNumber}
            round={round}
            errors={
              roundSaveAttempted[round.roundNumber]
                ? roundValidationByNumber[round.roundNumber]
                : undefined
            }
            disabled={formDisabled}
            savingSection={savingSection}
            onAddVariation={() => handleAddVariation(round.roundNumber)}
            onSaveRound={() => handleSaveRound(round.roundNumber)}
            onRemoveVariation={(variationIndex) =>
              handleRemoveVariation(round.roundNumber, variationIndex)
            }
            onVariationNameChange={(variationIndex, value) =>
              handleVariationNameChange(
                round.roundNumber,
                variationIndex,
                value,
              )
            }
            onCopyModeChange={(variationIndex, field, mode) =>
              handleCopyModeChange(
                round.roundNumber,
                variationIndex,
                field,
                mode,
              )
            }
            onCopyPromptChange={(variationIndex, field, prompt) =>
              handleCopyPromptChange(
                round.roundNumber,
                variationIndex,
                field,
                prompt,
              )
            }
            onSignatureModeChange={(variationIndex, field, mode) =>
              handleSignatureModeChange(
                round.roundNumber,
                variationIndex,
                field,
                mode,
              )
            }
          />
        ))}
      </div>
      <ConfirmModal
        open={resetOverrideOpen}
        onClose={() => setResetOverrideOpen(false)}
        onConfirm={handleConfirmResetOverride}
        message={
          <span>
            Reset this sequence to the system default prompts? Unsaved prompt
            changes will be lost.
          </span>
        }
      />
    </>
  )
}

export default SequencePromptsPanel
